/* Shared chrome ported from the KBL prototype: toasts, modals, confirm dialogs,
 * page heads, KPI tiles, empty states and the small formatting helpers the
 * prototype used everywhere (ago, dateHuman, nf).
 *
 * The prototype renders these as global functions writing into fixed DOM nodes;
 * here they are React contexts + components with the exact same class names so
 * prototype.css styles them identically.
 *
 * ACCESSIBILITY. The dialog and toast surfaces are Radix primitives rather than
 * hand-rolled DOM. They were hand-rolled first, and the versions below keep the
 * identical public API (`useModal`, `useToast`, `useConfirm`) and the identical
 * class names, so no call site changed. prototype.css needed one addition — see its
 * RADIX COMPAT block — because Radix portals dialog content as a sibling of the
 * overlay rather than a child, which the old centring relied on. What Radix
 * adds is the part that is tedious to get right and invisible when it is wrong:
 * focus is trapped inside an open dialog and restored to the trigger on close,
 * the rest of the page is inert and hidden from screen readers, background
 * scroll is locked, Escape and outside-click close through one code path, and
 * the title/description are wired to aria-labelledby / aria-describedby.
 * Toasts announce through a live region and are reachable with F6.
 *
 * Prefer a Radix primitive over a new hand-rolled one for any surface that
 * traps focus or floats above the page — dropdowns, popovers, tooltips. */

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import * as Toast from '@radix-ui/react-toast'

import { Icon, type IconName } from './icons'

/* ------------------------------------------------------------------ format */

export function nf(n: number): string {
  return Number(n).toLocaleString('en-IN')
}

export function ago(iso: string | number | null): string {
  if (!iso) return '—'
  const ts = typeof iso === 'number' ? iso : new Date(iso).getTime()
  const mins = Math.floor((Date.now() - ts) / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins} min ago`
  if (mins < 60 * 24) return `${Math.floor(mins / 60)} hr ago`
  return `${Math.floor(mins / (60 * 24))} d ago`
}

export function dateHuman(s: string | null): string {
  if (!s) return '—'
  const d = new Date(s)
  if (Number.isNaN(d.getTime())) return s
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
}

export function clockTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })
}

export function initialsOf(name: string): string {
  return name
    .replace(/\(.*?\)/g, '')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase()
}

/* ------------------------------------------------------------------ toasts */

type ToastKind = 'ok' | 'err' | 'warn' | 'info'

interface ToastItem {
  id: number
  kind: ToastKind
  title: string
  desc?: string
  /** Caller-supplied lifetime, handed to Radix as the root's `duration`. */
  ms: number
}

const TOAST_ICON: Record<ToastKind, IconName> = {
  ok: 'checkCircle',
  err: 'alert',
  warn: 'warn',
  info: 'info',
}

type ToastFn = (kind: ToastKind, title: string, desc?: string, ms?: number) => void

const ToastContext = createContext<ToastFn>(() => {})

export function useToast() {
  return useContext(ToastContext)
}

let toastSeq = 0

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([])

  const remove = useCallback((id: number) => {
    setItems((current) => current.filter((t) => t.id !== id))
  }, [])

  const toast = useCallback<ToastFn>((kind, title, desc, ms = 4200) => {
    const id = ++toastSeq
    setItems((current) => [...current, { id, kind, title, desc, ms }])
  }, [])

  return (
    <ToastContext.Provider value={toast}>
      {/* `duration` per-root, so a caller-supplied ms still wins. An error toast gets
       * longer on the clock than a success one only because the caller says so. */}
      <Toast.Provider swipeDirection="right">
        {children}
        {items.map((t) => (
          <Toast.Root
            key={t.id}
            duration={t.ms}
            className={`toast ${t.kind}`}
            /* Radix removes the node on close, whatever closed it — the timer, the
             * dismiss button, or a swipe. `.out` is left to CSS via data-state so the
             * exit animation is no longer a setTimeout racing the unmount. */
            onOpenChange={(open) => {
              if (!open) remove(t.id)
            }}
          >
            <span className="ti">
              <Icon name={TOAST_ICON[t.kind]} size={18} />
            </span>
            <div style={{ minWidth: 0, flex: 1 }}>
              <Toast.Title className="tt">{t.title}</Toast.Title>
              {t.desc && <Toast.Description className="td">{t.desc}</Toast.Description>}
            </div>
            <Toast.Close className="x-btn" aria-label="Dismiss notification">
              <Icon name="x" size={14} />
            </Toast.Close>
          </Toast.Root>
        ))}
        {/* Keeps the prototype's fixed bottom-right stack, and becomes the F6 landmark. */}
        <Toast.Viewport id="toasts" />
      </Toast.Provider>
    </ToastContext.Provider>
  )
}

/* ------------------------------------------------------------------ modal */

export interface ModalSpec {
  title: ReactNode
  sub?: ReactNode
  size?: 'narrow' | 'wide'
  body: ReactNode
  footer?: ReactNode | false
}

interface ModalContextValue {
  openModal: (spec: ModalSpec) => void
  closeModal: () => void
}

const ModalContext = createContext<ModalContextValue>({
  openModal: () => {},
  closeModal: () => {},
})

export function useModal() {
  return useContext(ModalContext)
}

export function ModalProvider({ children }: { children: ReactNode }) {
  const [spec, setSpec] = useState<ModalSpec | null>(null)

  const closeModal = useCallback(() => setSpec(null), [])
  const openModal = useCallback((next: ModalSpec) => setSpec(next), [])

  const value = useMemo(() => ({ openModal, closeModal }), [openModal, closeModal])

  return (
    <ModalContext.Provider value={value}>
      {children}
      {/* One modal at a time, as before. Radix owns Escape, outside-click, the focus
       * trap and the scroll lock — the unconditional `document` keydown listener this
       * used to register (it ran whether or not a dialog was open) is gone. */}
      <Dialog.Root
        open={spec !== null}
        onOpenChange={(open) => {
          if (!open) closeModal()
        }}
      >
        <Dialog.Portal>
          {/* `.on` is unconditional: Radix only mounts the overlay while open, so the
           * class that used to toggle `display:flex` is now simply always correct. */}
          <Dialog.Overlay className="modal-bg on" />
          <Dialog.Content className={`modal${spec?.size ? ` ${spec.size}` : ''}`}>
            <div className="modal-h">
              <div style={{ minWidth: 0 }}>
                <Dialog.Title asChild>
                  <h3>{spec?.title}</h3>
                </Dialog.Title>
                {/* Rendered only when the caller supplies one. This Radix version sets
                 * aria-describedby from `descriptionPresent`, so omitting the description
                 * omits the attribute — there is no dangling reference and no warning to
                 * suppress. An earlier revision rendered a hidden <span> echoing the title
                 * to silence a warning that this version does not raise; it pointed
                 * aria-describedby at an element `hidden` had already removed from the
                 * accessibility tree, which announced nothing while looking deliberate. */}
                {spec?.sub && (
                  <Dialog.Description className="sub">{spec.sub}</Dialog.Description>
                )}
              </div>
              <Dialog.Close className="x-btn" aria-label="Close">
                <Icon name="x" size={18} />
              </Dialog.Close>
            </div>
            <div className="modal-b">{spec?.body}</div>
            {spec?.footer !== false && (
              <div className="modal-f">
                {spec?.footer ?? (
                  <Dialog.Close className="btn btn-soft">Close</Dialog.Close>
                )}
              </div>
            )}
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </ModalContext.Provider>
  )
}

/** The prototype's confirmDlg(title, msg, okLabel, fn, danger). */
export function useConfirm() {
  const { openModal, closeModal } = useModal()
  return useCallback(
    (title: string, msg: ReactNode, okLabel: string, fn: () => void, danger = false) => {
      openModal({
        title,
        size: 'narrow',
        body: <p>{msg}</p>,
        footer: (
          <>
            <button type="button" className="btn btn-soft" onClick={closeModal}>
              Cancel
            </button>
            <button
              type="button"
              className={`btn${danger ? ' btn-danger' : ''}`}
              onClick={() => {
                closeModal()
                fn()
              }}
            >
              {okLabel}
            </button>
          </>
        ),
      })
    },
    [openModal, closeModal],
  )
}

/* ------------------------------------------------------------ page pieces */

/** The prototype's ahead(title, sub, actions). */
export function PageHead({
  title,
  sub,
  actions,
}: {
  title: ReactNode
  sub?: ReactNode
  actions?: ReactNode
}) {
  return (
    <div className="page-head">
      <div className="ph-l">
        <h1>{title}</h1>
        {sub && <div className="sub">{sub}</div>}
      </div>
      {actions && <div className="ph-r">{actions}</div>}
    </div>
  )
}

export function Kpi({
  tone,
  label,
  value,
  desc,
  hoverDesc,
}: {
  tone?: 'green' | 'info' | 'warn' | 'danger' | 'purple'
  label: ReactNode
  value: ReactNode
  desc?: ReactNode
  /** Explanatory line kept off the tile until the admin hovers it — detail on demand. */
  hoverDesc?: ReactNode
}) {
  return (
    <div className={`kpi${tone ? ` k-${tone}` : ''}`}>
      <div className="kl">{label}</div>
      <div className="kv">{value}</div>
      {desc && <div className="kd">{desc}</div>}
      {hoverDesc && <div className="kd kd-hover">{hoverDesc}</div>}
    </div>
  )
}

/** The prototype's emptyState(ic, title, msg, action). */
export function EmptyState({
  icon,
  title,
  msg,
  action,
}: {
  icon: IconName
  title: string
  msg: string
  action?: ReactNode
}) {
  return (
    <div className="empty">
      <div className="ic">
        <Icon name={icon} size={30} />
      </div>
      <h4>{title}</h4>
      <p>{msg}</p>
      {action}
    </div>
  )
}

export function Card({
  icon,
  title,
  hint,
  actions,
  tight,
  children,
  className,
}: {
  icon?: IconName
  title?: ReactNode
  hint?: ReactNode
  actions?: ReactNode
  tight?: boolean
  children: ReactNode
  className?: string
}) {
  return (
    <div className={`card${className ? ` ${className}` : ''}`}>
      {(title || actions) && (
        <div className="card-h">
          <h3>
            {icon && <Icon name={icon} size={16} />}
            {title}
            {hint && <span className="hint">{hint}</span>}
          </h3>
          {actions}
        </div>
      )}
      <div className={`card-b${tight ? ' tight' : ''}`}>{children}</div>
    </div>
  )
}

/** Skeleton rows while a list loads — prototype .skel. */
export function Skeleton({ rows = 4 }: { rows?: number }) {
  return (
    <div aria-busy="true" style={{ padding: 14 }}>
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="skel skel-row" style={{ width: `${88 - i * 9}%` }} />
      ))}
    </div>
  )
}

/* --------------------------------------------------------------- API error */

export function errorMessage(cause: unknown): string {
  if (cause instanceof Error) return cause.message
  return 'Something went wrong.'
}
